/* eslint-disable camelcase */
/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import withTheme from '@mui/styles/withTheme';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { sortFunction } from '../../../utils/orders';
import { STEPS_PROGRESS } from '../../../constants/Orders';

function StatusColumnCell({ statusData, theme }) {
	const defaultGrey = theme.palette.statuses.main;
	const defaultDanger = theme.palette.statuses.danger;
	const { t } = useTranslation('orders');

	const normalizedHistory = (statusData?.statusHistory || []).map((item) => ({
		Order_Status: item?.status,
		BOL_Number: item?.bol || '',
	}));

	const orderStatus = normalizedHistory.sort(sortFunction);

	const newOrderStatus = () => {
		const status = normalizedHistory.map((item) => item.Order_Status);
		if (status.includes('Cancelled') && statusData?.status === 'Cancelled') {
			return orderStatus;
		} else {
			return orderStatus.slice(0, -1);
		}
	};

	const unique = [
		...new Map(orderStatus.map((m) => [m.BOL_Number, m.Order_Status])).values(),
	].filter((n) => n);

	const uniqueCancel = [
		...new Map(
			newOrderStatus().map((m) => [m.BOL_Number, m.Order_Status])
		).values(),
	].filter((n) => n);

	const getCancelledShipment = (bol) => {
		let cancelledShipment = '';
		if (!statusData?.shippingInfo) {
			return '';
		}
		for (let i = 0; i < statusData.shippingInfo.length; i += 1) {
			if (
				statusData.shippingInfo[i].status !== null &&
				statusData.shippingInfo[i].status === 'Cancelled' &&
				statusData.shippingInfo[i].bol === bol
			) {
				cancelledShipment = 'Cancelled';
			}
		}
		return cancelledShipment;
	};

	const valueArray = () => {
		const status = normalizedHistory.map((item) => item.Order_Status);
		if (getCancelledShipment() === 'Cancelled') {
			return statusData.shippingInfo.map((item) => item.status);
		} else if (
			status.includes('Cancelled') &&
			statusData?.status !== 'Cancelled'
		) {
			return uniqueCancel;
		} else {
			return unique;
		}
	};

	const getStatusColor = (value, key) => {
		const status = STEPS_PROGRESS[value] || {
			progress: 0,
			color: () => defaultGrey,
		};

		if (key === 0 && value === 'Alert') {
			return theme.palette.statuses.success;
		} else if (key === 0 && value === 'Cancelled') {
			return defaultDanger;
		}

		return status.progress >= key ? status.color(theme) : defaultGrey;
	};

	// using array indexes as a key fine in this case and there are no alternatives
	return valueArray().map((value, index) => {
		const key =
			typeof value === 'object'
				? `${value.BOL_Number}-${value.Order_Status}`
				: `status-${value}-${index}`; // fallback for primitive strings

		const statusKey = value.Order_Status ? value.Order_Status : value;

		return (
			<Typography component="div" key={key} aria-label="status">
				{new Array(5).fill(true).map((_, i) => (
					<span key={i}>
						<FiberManualRecordIcon
							aria-label="dot"
							role="graphics-document"
							aria-hidden="true"
							style={{
								fontSize: '11px',
								color: getStatusColor(statusKey, i),
							}}
						/>
					</span>
				))}
				<Grid item mt={-1} aria-label={t(`statuses.${statusKey}`)}>
					{t(`statuses.${statusKey}`)}
				</Grid>
			</Typography>
		);
	});
}

StatusColumnCell.propTypes = {
	statusData: PropTypes.shape({
		status: PropTypes.string,
		statusHistory: PropTypes.arrayOf(
			PropTypes.shape({
				status: PropTypes.string,
				bol: PropTypes.string,
			})
		),
		shippingInfo: PropTypes.arrayOf(
			PropTypes.shape({
				status: PropTypes.string,
				bol: PropTypes.oneOfType([
					PropTypes.string,
					PropTypes.arrayOf(PropTypes.string),
				]),
			})
		),
	}),
	theme: PropTypes.any.isRequired,
};

export default React.memo(withTheme(StatusColumnCell));
